import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Paper,
  Skeleton,
  Chip,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Business,
  CreditCard,
  People,
  AttachMoney,
  Warning,
  SupportAgent,
  TrendingUp,
  CalendarMonth,
  Block,
} from '@mui/icons-material';
import { superAdminApi } from '../../../api/superAdminApi';
import { SuperAdminDashboardData } from '../../../types/superAdminTypes';

const SuperAdminDashboardPage: React.FC = () => {
  const [data, setData] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const result = await superAdminApi.getDashboard();
        debugger;
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const statCards = [
    {
      title: 'Total Tenants',
      value: data?.totalTenants ?? 0,
      icon: <Business sx={{ fontSize: 32 }} />,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: 'Active Subscriptions',
      value: data?.activeSubscriptions ?? 0,
      icon: <CreditCard sx={{ fontSize: 32 }} />,
      color: '#4caf50',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
      title: 'Expired Subscriptions',
      value: data?.expiredSubscriptions ?? 0,
      icon: <Warning sx={{ fontSize: 32 }} />,
      color: '#f44336',
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    },
    {
      title: 'Suspended Tenants',
      value: data?.suspendentTenants ?? 0,
      icon: <Block sx={{ fontSize: 32 }} />,
      color: '#f44336',
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
    },
    {
      title: 'Total Employees',
      value: data?.totalEmployees ?? 0,
      icon: <People sx={{ fontSize: 32 }} />,
      color: '#ff9800',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      title: 'Monthly Revenue',
      value: `₹${(data?.monthlyRevenue ?? 0).toLocaleString()}`,
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
      color: '#9c27b0',
      gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
      title: 'Pending Payments',
      value: data?.pendingPayments ?? 0,
      icon: <Warning sx={{ fontSize: 32 }} />,
      color: '#ff5722',
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
      title: 'Open Tickets',
      value: data?.openTickets ?? 0,
      icon: <SupportAgent sx={{ fontSize: 32 }} />,
      color: '#00bcd4',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
  ];

  const maxPlanCount = data?.planDistribution?.length
    ? Math.max(...data.planDistribution.map((p) => p.count))
    : 1;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
          Super Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' } }}>
          Platform overview and key metrics across all tenants.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
            {loading ? (
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            ) : (
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  height: '100%',
                  cursor: 'default',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 25px ${card.color}25`,
                  },
                }}
              >
                <Box sx={{ height: 3, background: card.gradient }} />
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                  <Box
                    sx={{
                      background: `${card.color}14`,
                      borderRadius: 2.5,
                      minWidth: { xs: 36, sm: 48 },
                      height: { xs: 36, sm: 48 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: card.color,
                      '& .MuiSvgIcon-root': { fontSize: { xs: 22, sm: 32 } },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800} lineHeight={1.1} sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap sx={{ mt: 0.25, display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {card.title}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        ))}
      </Grid>

      {/* Revenue Growth + Plan Distribution */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
        {/* Revenue Growth */}
        <Grid size={{ xs: 12, md: 7 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Revenue Growth
                </Typography>
                <Chip icon={<TrendingUp sx={{ fontSize: 16 }} />} label="Trend" size="small" color="primary" variant="outlined" />
              </Box>
              {data?.revenueGrowth?.length ? (
                data.revenueGrowth.map((item) => (
                  <Box key={item.month} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>{item.month}</Typography>
                      <Typography variant="body2" color="text.secondary">₹{item.amount.toLocaleString()}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((item.amount / Math.max(...data.revenueGrowth.map(r => r.amount), 1)) * 100, 100)}
                      sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } }}
                    />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>No revenue data available</Typography>
              )}
            </Paper>
          )}
        </Grid>

        {/* Plan Distribution */}
        <Grid size={{ xs: 12, md: 5 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Plan Distribution
                </Typography>
                <Chip icon={<CalendarMonth sx={{ fontSize: 16 }} />} label="Current" size="small" color="secondary" variant="outlined" />
              </Box>
              {data?.planDistribution?.length ? (
                data.planDistribution.map((plan) => (
                  <Box key={plan.planName} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{plan.planName}</Typography>
                      <Typography variant="body2" color="text.secondary">{plan.count} tenants</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(plan.count / maxPlanCount) * 100}
                      sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 5, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' } }}
                    />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>No plan data available</Typography>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Tenant Growth */}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12 }}>
          {loading ? (
            <Skeleton variant="rounded" height={250} />
          ) : (
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Tenant Growth
              </Typography>
              {data?.tenantGrowth?.length ? (
                <Grid container spacing={2}>
                  {data.tenantGrowth.map((item) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={item.month}>
                      <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="h5" fontWeight="bold" color="primary">{item.count}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.month}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary" textAlign="center" py={4}>No tenant growth data available</Typography>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminDashboardPage;
